/**
 * RUT chileno: formato y digito verificador (CHK-04).
 *
 * Se valida en el navegador y no solo al facturar porque la boleta o factura se
 * emite despues de cobrar, y un RUT mal escrito se descubriria con la venta ya
 * hecha. El modulo 11 atrapa el tipeo antes: es aritmetica, no una consulta al SII, asi
 * que no dice si el RUT existe ni de quien es —solo que esta bien escrito—.
 *
 * Funciones puras, sin React y sin DOM.
 */

/** Deja solo digitos y la K final, en mayuscula. */
function limpiar(rut: string): string {
  return rut.replace(/[^0-9kK]/g, '').toUpperCase();
}

/**
 * Digito verificador de un cuerpo numerico, por modulo 11.
 *
 * Se recorre de derecha a izquierda multiplicando por la serie 2..7 ciclica.
 */
function digitoVerificador(cuerpo: string): string {
  let suma = 0;
  let multiplicador = 2;

  for (let posicion = cuerpo.length - 1; posicion >= 0; posicion -= 1) {
    suma += Number(cuerpo[posicion]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }

  const resto = 11 - (suma % 11);
  if (resto === 11) return '0';
  if (resto === 10) return 'K';
  return String(resto);
}

export function rutEsValido(rut: string): boolean {
  const limpio = limpiar(rut);
  // Menos de siete digitos no es un RUT: son 7 u 8 mas el verificador.
  if (limpio.length < 8 || limpio.length > 9) return false;

  const cuerpo = limpio.slice(0, -1);
  const verificador = limpio.slice(-1);

  if (!/^\d+$/.test(cuerpo)) return false;

  return digitoVerificador(cuerpo) === verificador;
}

/**
 * Formato chileno con puntos y guion: 12.345.678-5.
 *
 * Se aplica mientras se escribe, asi que tiene que tolerar un RUT a medio
 * escribir sin pelear con quien todavia no termino de tipear.
 */
export function formatearRut(rut: string): string {
  const limpio = limpiar(rut);
  if (limpio.length === 0) return '';
  if (limpio.length === 1) return limpio;

  const cuerpo = limpio.slice(0, -1);
  const verificador = limpio.slice(-1);
  const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${conPuntos}-${verificador}`;
}
